import { Injectable } from '@angular/core';
import { Charge, Exception } from '@cargos/sprintpay-models';
import { FlightNumberService, StorageService, StorageTypes } from '@cargos/sprintpay-services';
import { BehaviorSubject, Observable, map, of, switchMap, throwError } from 'rxjs';
import { CurrentFacilityService } from './current-facility.service';
import { FlightService } from './flight.service';
import { SummaryService } from './summary/summary.service';

@Injectable({
    providedIn: 'root',
})
export class SearchByFlightService {
    private _currentPayment: BehaviorSubject<Charge[] | null> = new BehaviorSubject<Charge[] | null>([]);

    constructor(
        private storageService: StorageService,
        private flightNumberService: FlightNumberService,
        private currentFacilityService: CurrentFacilityService,
        private flightService: FlightService,
        private summaryService: SummaryService
    ) {
        const flight_info: Charge[] = this.storageService.getElement('flight_info', StorageTypes.SESSION);
        if (flight_info) {
            this.setAndSaveOnStorageFlightInfo(flight_info);
        }
    }

    get instant_current_payment(): Charge[] | null {
        return this._currentPayment.value;
    }

    getCurrentPayment$(): Observable<Charge[] | null> {
        return this._currentPayment.asObservable();
    }

    private setFlightInfo(currentPayment: Charge[] | null): void {
        this._currentPayment.next(currentPayment);
    }

    saveFlightInfo(flightInfo: Charge[] | null): void {
        this.storageService.saveElement('flight_info', flightInfo, StorageTypes.SESSION);
    }

    setAndSaveOnStorageFlightInfo(flightInfo: Charge[] | null, flightNumber?: string, flightDate?: string): void {
        this.setFlightInfo(flightInfo);
        this.saveFlightInfo(flightInfo);

        this.summaryService.setCostCenter(flightInfo?.[0]?.costCenter || '');

        this.flightService.setFlightDestination(flightInfo?.[0]?.destination || '');
        flightDate && this.flightService.setFlightDate(flightDate);
        flightNumber && this.flightService.setFlightNumber(flightNumber);
    }

    getChargeByFlight(flightNumberValue: string, flightDate: string, pickupDate: string): Observable<Charge[]> {
        const facilitySource = this.currentFacilityService.getFacilitySource();

        return this.flightNumberService.chargeByflight(flightNumberValue, pickupDate, flightDate, facilitySource).pipe(
            map((charges: Charge[]) => {
                this.setAndSaveOnStorageFlightInfo(charges, flightNumberValue, flightDate);

                if (charges?.length) {
                    this.summaryService.setAndSaveLocationInStorage(charges[0]?.destination || '');
                }

                return charges;
            }),
            switchMap((charges: Charge[]) => {
                if (!charges.length) {
                    return throwError(() =>
                        Exception.fromJson({
                            title: 'Flight not found',
                            description:
                                'Please try again, or contact your airport representative for assistance with your flight number.',
                            type: 'warning',
                        })
                    );
                }

                return of(charges);
            })
        );
    }

    findChargeDetail(status: string): Charge | null {
        return this.instant_current_payment?.find((flight) => !!flight[status]) || null;
    }

    isAWBReceivedFromFlight(): boolean {
        return !!this.findChargeDetail('isReceivedFromFlight');
    }
}
